import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Stage } from '../api/stages';
import { openNavigation } from '../utils/navigate';
import { StageWeatherBadge } from './StageWeatherBadge';

// Default center: centro Italia (Bologna)
const DEFAULT_CENTER: [number, number] = [44.5, 11.3];
const DEFAULT_ZOOM = 6;

function makeStageIcon(n: number): L.DivIcon {
  return L.divIcon({
    className: 'cd-marker',
    html: `<div class="cd-marker-pin"><span>${n}</span></div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

const PICK_ICON: L.DivIcon = L.divIcon({
  className: 'cd-marker cd-marker-pick',
  html: `<div class="cd-marker-pin"><span>＋</span></div>`,
  iconSize: [32, 40],
  iconAnchor: [16, 40],
});

const LODGING_ICON: L.DivIcon = L.divIcon({
  className: 'cd-marker cd-marker-lodging',
  html: `<div class="cd-marker-pin cd-marker-pin-lodging"><span>🛏️</span></div>`,
  iconSize: [30, 38],
  iconAnchor: [15, 38],
  popupAnchor: [0, -34],
});

type LodgingPoint = { id: string; title: string; lat: number; lon: number };

/** Alloggi usati come punto di routing della tappa (flag attivo + coordinate). */
function lodgingRoutePoints(stages: Stage[]): LodgingPoint[] {
  return stages
    .filter(
      (s) => s.lodging.useForRouting && s.lodging.lat !== null && s.lodging.lon !== null,
    )
    .map((s) => ({
      id: s.id,
      title: s.lodging.name ?? s.title,
      lat: s.lodging.lat!,
      lon: s.lodging.lon!,
    }));
}

function FitBounds({ stages, pick }: { stages: Stage[]; pick: { lat: number; lon: number } | null }) {
  const map = useMap();
  const lodgings = lodgingRoutePoints(stages);
  useEffect(() => {
    const points: [number, number][] = [
      ...stages.map((s) => [s.lat, s.lon] as [number, number]),
      ...lodgings.map((l) => [l.lat, l.lon] as [number, number]),
      ...(pick ? [[pick.lat, pick.lon] as [number, number]] : []),
    ];
    if (points.length === 0) return;
    if (points.length === 1) {
      const p = points[0]!;
      map.setView(p, Math.max(map.getZoom(), 11));
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stages.map((s) => `${s.id}:${s.lat}:${s.lon}`).join('|'),
    lodgings.map((l) => `${l.id}:${l.lat}:${l.lon}`).join('|'),
    pick?.lat,
    pick?.lon,
  ]);
  return null;
}

function ClickPicker({ onPick }: { onPick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export type TripMapProps = {
  tripId: string;
  stages: Stage[];
  pick?: { lat: number; lon: number } | null;
  onPick?: (lat: number, lon: number) => void;
  /** Polyline OSRM in formato [lon, lat][] (GeoJSON). Se passata, viene
   *  disegnata come linea continua sopra la dashed delle tappe. */
  routePolyline?: [number, number][] | null;
  height?: number | string;
};

export function TripMap({
  tripId,
  stages,
  pick = null,
  onPick,
  routePolyline = null,
  height = 380,
}: TripMapProps) {
  const initialCenter = useMemo<[number, number]>(() => {
    if (stages[0]) return [stages[0].lat, stages[0].lon];
    if (pick) return [pick.lat, pick.lon];
    return DEFAULT_CENTER;
  }, [stages, pick]);

  const polyline = useMemo<[number, number][]>(
    () => stages.map((s) => [s.lat, s.lon]),
    [stages],
  );

  return (
    <div className={`map-wrap${onPick ? ' picking' : ''}`} style={{ height }}>
      <MapContainer
        center={initialCenter}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={19}
        />
        <FitBounds stages={stages} pick={pick} />
        {onPick && <ClickPicker onPick={onPick} />}

        {/* Quando abbiamo la rotta stradale OSRM la disegniamo solida; altrimenti
            ripieghiamo sulla linea retta tratteggiata. */}
        {routePolyline && routePolyline.length >= 2 ? (
          <Polyline
            // OSRM dà [lon, lat]; Leaflet vuole [lat, lon]
            positions={routePolyline.map(([lon, lat]) => [lat, lon] as [number, number])}
            // dashArray: '' va passato esplicitamente — Leaflet.setStyle non
            // azzera le proprieta omesse, quindi senza questo la dashArray
            // della fallback tratteggiata resterebbe applicata.
            pathOptions={{ color: '#1565c0', weight: 5, opacity: 0.9, dashArray: '' }}
          />
        ) : polyline.length >= 2 ? (
          <Polyline
            positions={polyline}
            pathOptions={{ color: '#1a3a52', weight: 3, opacity: 0.45, dashArray: '6 6' }}
          />
        ) : null}

        {stages.map((s, i) => (
          <Marker key={s.id} position={[s.lat, s.lon]} icon={makeStageIcon(i + 1)}>
            <Popup>
              <div className="popup">
                <strong>
                  {i + 1}. {s.title}
                </strong>
                {s.address && <div className="muted">{s.address}</div>}
                <div className="coords">
                  {s.lat.toFixed(5)}, {s.lon.toFixed(5)}
                </div>
                <StageWeatherBadge
                  tripId={tripId}
                  stageId={s.id}
                  plannedDate={s.plannedDate}
                  compact
                />
                <button
                  className="btn btn-primary popup-btn"
                  onClick={() => openNavigation(s.lat, s.lon, s.title)}
                >
                  Naviga
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {lodgingRoutePoints(stages).map((p) => (
          <Marker key={`lodging-${p.id}`} position={[p.lat, p.lon]} icon={LODGING_ICON}>
            <Popup>
              <div className="popup">
                <strong>🛏️ {p.title}</strong>
                <div className="muted">Alloggio (usato per il calcolo dei km)</div>
                <div className="coords">
                  {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {pick && (
          <Marker position={[pick.lat, pick.lon]} icon={PICK_ICON}>
            <Popup>
              <div className="popup">
                <strong>Posizione scelta</strong>
                <div className="coords">
                  {pick.lat.toFixed(5)}, {pick.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
      {onPick && (
        <div className="map-hint">📍 Clicca sulla mappa per scegliere la posizione</div>
      )}
    </div>
  );
}
